import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ParticleHero from './ParticleHero';
import { Features } from './ui/features-1';
import { BentoPricing } from './ui/bento-pricing';
import CTAWithVerticalMarquee from './ui/cta-with-text-marquee';
import UptimeSection from './ui/uptime-section';
import { SlidingTestimonials } from './ui/sliding-testimonial';
import SecuritySection from './ui/security-section';
import ContactCTA from './ui/contact-cta';
import { Zap, Wallet, Users } from 'lucide-react';
import './Home.css';

function asTranslationArray(value) {
  return Array.isArray(value) ? value : [];
}

const Home = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const launchFeatures = useMemo(
    () => asTranslationArray(t('home.pricing.launch.features', { returnObjects: true })),
    [t, i18n.language],
  );
  const scaleFeatures = useMemo(
    () => asTranslationArray(t('home.pricing.scale.features', { returnObjects: true })),
    [t, i18n.language],
  );
  const enterpriseFeatures = useMemo(
    () => asTranslationArray(t('home.pricing.enterprise.features', { returnObjects: true })),
    [t, i18n.language],
  );

  const homeBentoStackedCards = useMemo(
    () => [
      {
        titleBadge: t('home.pricing.cards.starter.badge'),
        priceLabel: t('home.pricing.cards.starter.priceLabel'),
        priceSuffix: t('home.pricing.cards.starter.priceSuffix'),
        features: launchFeatures,
        cta: t('home.pricing.cards.starter.cta'),
      },
      {
        titleBadge: t('home.pricing.cards.scale.badge'),
        priceLabel: t('home.pricing.cards.scale.priceLabel'),
        priceSuffix: t('home.pricing.cards.scale.priceSuffix'),
        features: scaleFeatures,
        cta: t('home.pricing.cards.scale.cta'),
      },
      {
        titleBadge: t('home.pricing.cards.enterprise.badge'),
        priceLabel: t('home.pricing.cards.enterprise.priceLabel'),
        priceSuffix: '',
        features: enterpriseFeatures,
        cta: t('home.pricing.cards.enterprise.cta'),
      },
    ],
    [t, i18n.language, launchFeatures, scaleFeatures, enterpriseFeatures],
  );

  const marqueeItems = useMemo(
    () => asTranslationArray(t('home.ctaMarquee.items', { returnObjects: true })),
    [t, i18n.language],
  );

  const testimonials = useMemo(() => [
    {
      id: 1,
      testimonial: t('testimonials.sarah.content'),
      author: `${t('testimonials.sarah.name')} — ${t('testimonials.sarah.role')}`,
    },
    {
      id: 2,
      testimonial: t('testimonials.mike.content'),
      author: `${t('testimonials.mike.name')} — ${t('testimonials.mike.role')}`,
    },
    {
      id: 3,
      testimonial: t('testimonials.lisa.content'),
      author: `${t('testimonials.lisa.name')} — ${t('testimonials.lisa.role')}`,
    },
  ], [t, i18n.language]);

  return (
    <div className="home">
      <ParticleHero />

      {/* What We Offer Section */}
      <Features
        title={t('home.services.title')}
        subtitle={t('home.services.subtitle')}
        items={[
          {
            icon: <Zap className="size-6" aria-hidden />,
            title: t('home.services.highlights.quick.title'),
            description: t('home.services.highlights.quick.description'),
          },
          {
            icon: <Wallet className="size-6" aria-hidden />,
            title: t('home.services.highlights.nofees.title'),
            description: t('home.services.highlights.nofees.description'),
          },
          {
            icon: <Users className="size-6" aria-hidden />,
            title: t('home.services.highlights.scale.title'),
            description: t('home.services.highlights.scale.description'),
          },
        ]}
      />

      {/* Pricing — four tiers (featured Growth + Starter, Scale, Enterprise) */}
      <BentoPricing
        title={t('home.pricing.title')}
        subtitle={t('home.pricing.subtitle')}
        featuredBadge={t('home.pricing.growthBadge')}
        recommendedLabel={t('home.pricing.recommendedLabel')}
        featuredPrice={t('home.pricing.featuredPrice')}
        featuredPriceSuffix={t('home.pricing.featuredPriceSuffix')}
        featuredFeatures={launchFeatures}
        featuredCta={t('home.pricing.featuredCta')}
        stackedCards={homeBentoStackedCards}
      />

      {/* Get Started CTA Marquee */}
      <CTAWithVerticalMarquee
        title={t('home.ctaMarquee.title')}
        description={t('home.ctaMarquee.description')}
        primaryButtonText={t('home.ctaMarquee.primaryButton')}
        primaryHref="/start-project"
        secondaryButtonText={t('home.ctaMarquee.secondaryButton')}
        secondaryHref="/services"
        items={marqueeItems}
      />

      {/* Tech Stack Section */}
      {/* Uptime / Infrastructure Section */}
      <UptimeSection />

      {/* Testimonials — horizontal marquee */}
      <SlidingTestimonials
        title={t('home.testimonials.title')}
        subtitle={t('home.testimonials.subtitle')}
        testimonials={testimonials}
      />

      {/* Security Section */}
      <SecuritySection />

      {/* Contact CTA Section */}
      <ContactCTA
        eyebrow={t('home.contact.title')}
        headline={t('home.contact.headline')}
        description={t('home.contact.description')}
        buttonLabel={t('home.contact.button')}
        onClick={() => navigate('/start-project')}
      />
    </div>
  );
};

export default Home; 